import React from "react";
import PropTypes from "prop-types";
import ModalModern from "../ModalModern";
import ModernPopupHeader from "../common/ModernPopupHeader";
import ModernPopupBody from "../common/ModernPopupBody";
import SendInput from "../../forms/SendInput";
import GhostCommentCard from "../../loading/skeletons/GhostCommentCard";
import LoadingToRealTransitionWrapper from "../../../../components/ui/transitions/LoadingToRealTransitionWraper";
import { formatTime } from "../../../../utils/date/DateUtils";

function CommentModal({
  onClose,
  title,
  bgImage = null,
  comments = [],
  onSendComment,
  onCommentReaction,
  isSending = false,
  isLoading = false,
}) {
  return (
    <ModalModern onClose={onClose}>
      <ModernPopupHeader onClose={onClose}>
        <div
          className={`header-content-modern-row comment-modal-header-banner ${
            bgImage ? "has-bg" : ""
          }`}
          style={{
            backgroundImage: bgImage ? `url(${bgImage})` : "none",
          }}
        >
          <h3 className="transaction-popup-title comment-modal-title">
            {title}
          </h3>
        </div>
        <SendInput
          onSend={onSendComment}
          isSending={isSending}
          placeholder="What's on your mind?"
        />
      </ModernPopupHeader>

      <ModernPopupBody>
        <div className="mp-col-container" style={{ gap: "16px" }}>
          <LoadingToRealTransitionWrapper
            isLoading={isLoading}
            ghost={
              <React.Fragment>
                <GhostCommentCard />
                <GhostCommentCard />
                <GhostCommentCard />
              </React.Fragment>
            }
          >
            {comments.length === 0 ? (
              <p className="comment-empty-state">
                No comments yet. Be the first to share your thoughts!
              </p>
            ) : (
              comments.map((comment) => {
                const rawReaction = comment.user_reaction ?? comment.user_vote;
                const userReaction = rawReaction === 2 ? -1 : rawReaction;

                return (
                  <div
                    key={comment.id || comment.uuid}
                    className="comment-card-item"
                  >
                    {/* Avatar */}
                    <img
                      src={
                        comment.user?.avatar ||
                        `${process.env.PUBLIC_URL}/images/avatar_settings.svg`
                      }
                      alt="User Avatar"
                      className="comment-user-avatar"
                    />

                    {/* Comment Details */}
                    <div className="comment-details-container">
                      <div className="comment-meta-header">
                        <span className="comment-author-name">
                          {comment.user?.name || "Anonymous"}
                        </span>
                        <span className="comment-date-stamp">
                          {formatTime(comment.created_at)}
                        </span>
                      </div>

                      <p className="comment-text-body">
                        {comment.description || comment.text}
                      </p>

                      {/* Like / Dislike Container */}
                      <div
                        style={{
                          display: "flex",
                          width: "100%",
                          justifyContent: "flex-end",
                        }}
                      >
                        <div className="reactions-comments-container">
                          {/* Like Button */}
                          <div
                            className={`reactions-comments-item ${
                              userReaction === 1 ? "active" : ""
                            }`}
                            onClick={() =>
                              onCommentReaction &&
                              onCommentReaction(comment.uuid, 1)
                            }
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                onCommentReaction &&
                                  onCommentReaction(comment.uuid, 1);
                              }
                            }}
                            aria-label="Like Comment"
                          >
                            <img
                              src={`${process.env.PUBLIC_URL}/images/icons/${
                                userReaction === 1
                                  ? "like_004789_filled.svg"
                                  : "like_004789.svg"
                              }`}
                              alt="Like Icon"
                              loading="lazy"
                            />
                            <span>
                              {comment.up_votes || comment.likes || 0}
                            </span>
                          </div>

                          {/* Dislike Button */}
                          <div
                            className={`reactions-comments-item ${
                              userReaction === -1 ? "active" : ""
                            }`}
                            onClick={() =>
                              onCommentReaction &&
                              onCommentReaction(comment.uuid, -1)
                            }
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                onCommentReaction &&
                                  onCommentReaction(comment.uuid, -1);
                              }
                            }}
                            aria-label="Dislike Comment"
                          >
                            <img
                              src={`${process.env.PUBLIC_URL}/images/icons/${
                                userReaction === -1
                                  ? "dislike_004789_filled.svg"
                                  : "dislike_004789.svg"
                              }`}
                              alt="Dislike Icon"
                              loading="lazy"
                            />
                            <span>
                              {comment.down_votes || comment.dislikes || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </LoadingToRealTransitionWrapper>
        </div>
      </ModernPopupBody>
    </ModalModern>
  );
}

CommentModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  bgImage: PropTypes.string,
  comments: PropTypes.array,
  onSendComment: PropTypes.func,
  onCommentReaction: PropTypes.func,
  isSending: PropTypes.bool,
  isLoading: PropTypes.bool,
};

export default CommentModal;
